import type { RealtimeConnectionState, useConnectionStore } from '@/stores/connectionStore'
import { normalizeRealtimeConnectionState } from '@/stores/connectionStore'

type ConnectionStore = ReturnType<typeof useConnectionStore>

type StateChangePayload = {
  previous?: unknown
  current?: unknown
}

export type PusherConnection = {
  state?: unknown
  transport?: {
    name?: unknown
  } | null
  bind?: (event: string, callback: (payload: StateChangePayload) => void) => void
  unbind?: (event: string, callback: (payload: StateChangePayload) => void) => void
  bind_global?: (callback: (eventName: string, data?: unknown) => void) => void
  unbind_global?: (callback: (eventName: string, data?: unknown) => void) => void
}

function readTransportName(connection: PusherConnection): string | null {
  return typeof connection.transport?.name === 'string'
    ? connection.transport.name
    : null
}

export function monitorPusherConnection(
  connection: PusherConnection | null | undefined,
  store: ConnectionStore
): () => void {
  if (!connection) {
    store.setState('unavailable')
    return () => {}
  }

  store.setDiagnostics({ transport: readTransportName(connection) })

  if (typeof connection.state === 'string') {
    store.setState(normalizeRealtimeConnectionState(connection.state))
  }

  const handleStateChange = ({ current, previous }: StateChangePayload) => {
    const currentState: RealtimeConnectionState = normalizeRealtimeConnectionState(current)
    store.setState(currentState, previous)
    store.setDiagnostics({ transport: readTransportName(connection) })
  }

  const handleFrame = (eventName: string) => {
    if (eventName !== 'state_change') {
      store.markFrameReceived()
    }
  }

  connection.bind?.('state_change', handleStateChange)
  connection.bind_global?.(handleFrame)

  return () => {
    connection.unbind?.('state_change', handleStateChange)
    connection.unbind_global?.(handleFrame)
  }
}
